// Shared API client utilities

// In production, require VITE_API_BASE to be set - no fallback to staging
export const API_BASE = import.meta.env.VITE_API_BASE || (import.meta.env.PROD ? '' : 'https://fitcheck-staging-f135bb75d4a6.herokuapp.com')

// Allowed redirect domains for Stripe
const ALLOWED_REDIRECT_DOMAINS = ['checkout.stripe.com', 'billing.stripe.com']

/**
 * Validates a redirect URL is from a trusted domain.
 * Prevents open redirect vulnerabilities.
 */
export function isAllowedRedirectUrl(url) {
  if (!url) return false
  try {
    const parsed = new URL(url)
    if (parsed.protocol !== 'https:') return false
    return ALLOWED_REDIRECT_DOMAINS.some(domain => parsed.hostname === domain || parsed.hostname.endsWith('.' + domain))
  } catch {
    return false
  }
}

export const DEFAULT_TIMEOUT = 60000
export const GENERATION_TIMEOUT = 180000
export const VIDEO_GENERATION_TIMEOUT = 300000 // 5 min for video generation
export const IMAGE_FETCH_TIMEOUT = 90000

/**
 * Wraps fetch with automatic timeout using AbortController. Aborts the request
 * if it exceeds the specified timeout duration.
 *
 * @param {string} url - URL to fetch
 * @param {RequestInit} [options={}] - Fetch options (method, headers, body, etc.)
 * @param {number} [timeout=DEFAULT_TIMEOUT] - Timeout in milliseconds
 * @returns {Promise<Response>} Fetch response
 * @throws {Error} Throws if request times out or network fails
 */
export async function fetchWithTimeout(url, options = {}, timeout = DEFAULT_TIMEOUT) {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeout)

  try {
    const response = await fetch(url, { ...options, signal: controller.signal })
    return response
  } finally {
    clearTimeout(timeoutId)
  }
}

/**
 * Generic API call wrapper that handles auth headers, JSON serialization, error handling,
 * and timeouts. Reduces boilerplate across all API modules.
 *
 * @param {string} endpoint - API endpoint path (e.g., '/api/wardrobe')
 * @param {Object} options - Request configuration
 * @param {string} [options.token=null] - Auth token for Bearer header
 * @param {string} [options.method='GET'] - HTTP method
 * @param {Object} [options.body=null] - Request body (auto-serialized to JSON)
 * @param {string} [options.errorMessage='Request failed'] - Default error message
 * @param {number} [options.timeout=DEFAULT_TIMEOUT] - Request timeout in ms
 * @param {Object} [options.extraHeaders={}] - Additional headers to merge
 * @param {boolean} [options.parseResponse=true] - Whether to parse JSON response
 * @returns {Promise<Object|{success: boolean}>} Parsed JSON response or success object
 * @throws {Error} Enhanced error with status code and server error data
 */
export async function apiCall(endpoint, {
  token = null,
  method = 'GET',
  body = null,
  errorMessage = 'Request failed',
  timeout = DEFAULT_TIMEOUT,
  extraHeaders = {},
  parseResponse = true
} = {}) {
  const headers = { ...extraHeaders }
  if (token) headers['Authorization'] = `Bearer ${token}`
  if (body) headers['Content-Type'] = 'application/json'

  const options = { method, headers }
  if (body) options.body = JSON.stringify(body)

  const response = await fetchWithTimeout(`${API_BASE}${endpoint}`, options, timeout)

  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    const error = new Error(data.error || data.message || errorMessage)
    error.status = response.status
    error.data = data
    throw error
  }

  return parseResponse ? response.json() : { success: true }
}

/**
 * Fetches an image from URL and converts it to base64 data URL. Used for loading
 * generation results from S3 presigned URLs.
 *
 * @param {string} url - Image URL to fetch (typically S3 presigned URL)
 * @returns {Promise<string>} Base64-encoded data URL (e.g., 'data:image/png;base64,...')
 * @throws {Error} Throws if image fetch fails or FileReader conversion fails
 */
export async function fetchAndConvertImage(url) {
  const response = await fetchWithTimeout(url, {}, IMAGE_FETCH_TIMEOUT)
  if (!response.ok) throw new Error('Failed to fetch generated image')

  const blob = await response.blob()
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = () => reject(new Error('Failed to read image'))
    reader.readAsDataURL(blob)
  })
}

/**
 * Generic polling utility for long-running jobs. Handles the common polling pattern
 * used by generation, video, recommendations, and imports.
 *
 * @param {Object} config - Polling configuration
 * @param {string} config.url - Full URL to poll
 * @param {Function} config.getHeaders - Returns headers object (called each iteration for fresh tokens)
 * @param {number} [config.interval=2000] - Polling interval in ms
 * @param {number} [config.timeout=GENERATION_TIMEOUT] - Max polling duration in ms
 * @param {Function} [config.isComplete] - (data) => boolean, defaults to data.status === 'completed'
 * @param {Function} [config.isFailed] - (data) => boolean, defaults to data.status === 'failed'
 * @param {Function} [config.onFailed] - (data) => Error, custom error handler for failed status
 * @param {string} [config.timeoutMessage='Request timed out'] - Error message on timeout
 * @returns {Promise<Object>} Final response data
 */
export async function pollStatus({
  url,
  getHeaders,
  interval = 2000,
  timeout = GENERATION_TIMEOUT,
  isComplete = (data) => data.status === 'completed',
  isFailed = (data) => data.status === 'failed',
  onFailed = (data) => new Error(data.error || 'Request failed'),
  timeoutMessage = 'Request timed out'
}) {
  const startTime = Date.now()

  while (Date.now() - startTime < timeout) {
    const headers = await getHeaders()
    const response = await fetchWithTimeout(url, { headers })

    if (!response.ok) {
      if (response.status === 500) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error || 'Server error')
      }
      throw new Error('Failed to check status')
    }

    const data = await response.json()

    if (isComplete(data)) return data
    if (isFailed(data)) throw onFailed(data)

    await new Promise(resolve => setTimeout(resolve, interval))
  }

  throw new Error(timeoutMessage)
}

/**
 * Returns dev headers for rate limiting and watermark control.
 * Only active in development mode.
 */
export function getDevHeaders() {
  // Dev headers disabled in production
  if (import.meta.env.PROD) {
    return {}
  }
  const headers = {}
  if (localStorage.getItem('dev_simulate_rate_limit') === 'true') {
    headers['X-Dev-Simulate-Rate-Limit'] = 'true'
  }
  const watermarkEnabled = localStorage.getItem('dev_watermark_enabled')
  if (watermarkEnabled === 'false') {
    headers['X-No-Watermark'] = 'true'
  }
  return headers
}
